import * as React from "react";
import _ from "lodash";
import ReactMarkdown from "react-markdown";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import InlineList from "../utils/inline-list";
import PersonList from "../persons/person-list";
import AutoLink from "../utils/auto-link";
import Circles from "./circles";

function FeaturedImage({ image, title, description }) {
  const hasCaption = title || description;
  const hasTitleAndDesc = title && description;

  return !image ? null : (
    <>
      <div className="featured-image">
        <figure className="d-flex justify-content-center mx-auto">
          <GatsbyImage
            image={getImage(image)}
            alt={title || "Featured image"}
            title={title}
          />
        </figure>

        {!hasCaption ? null : (
          <figcaption className="container">
            <div className="row">
              <div className="col offset-lg-3 col-lg-9 text-muted">
                {!title ? null : (
                  <span className={`${hasTitleAndDesc ? "fw-bold" : null}`}>
                    <ReactMarkdown components={{ p: React.Fragment }}>
                      {title}
                    </ReactMarkdown>
                  </span>
                )}
                {!title || !description ? null : <span> — </span>}
                {!description ? null : (
                  <ReactMarkdown components={{ p: React.Fragment }}>
                    {description}
                  </ReactMarkdown>
                )}
              </div>
            </div>
          </figcaption>
        )}
      </div>
    </>
  );
}

function FeaturedImageNextToTitle({ imageObj = {} } = {}) {
  const { image, title, description } = imageObj;
  return !image ? null : (
    <div
      className="featured-image-next-to-title"
      style={{ maxHeight: "100vh" }}
    >
      <GatsbyImage
        image={getImage(image)}
        alt={title || description}
        title={title}
        className="fix-gatsby-image-wrapper"
        style={{ height: "100%", borderRadius: "50em" }}
      />
    </div>
  );
}

function Date({ date, dateModified }) {
  return !date && !dateModified ? null : (
    <div className="mb-4">
      <InlineList sep={<span className="opacity-50 mx-2">•</span>}>
        {[
          date && <span key="date">{date}</span>,
          dateModified && (
            <span className="opacity-50" key="modified">
              Last modified {dateModified}
            </span>
          ),
        ]}
      </InlineList>
    </div>
  );
}

function Title({ title, subtitle, tagline }) {
  return !title && !subtitle && !tagline ? null : (
    <div className="mb-5">
      <h1 className="display-1 serif mb-2">
        {!title ? null : (
          <span className="me-3">
            <ReactMarkdown components={{ p: React.Fragment }}>
              {title}
            </ReactMarkdown>
          </span>
        )}
        {!subtitle ? null : (
          <>
            <span className="subtitle text-primary fst-italic">
              <ReactMarkdown components={{ p: React.Fragment }}>
                {subtitle}
              </ReactMarkdown>
            </span>
          </>
        )}
      </h1>
      {!tagline ? null : <div className="lead">{tagline}</div>}
    </div>
  );
}

function People({ people, peopleLabel = "By", showLastName = true } = {}) {
  return !people ? null : (
    <div className="d-inline-flex gap-1 flex-wrap mb-5">
      {!peopleLabel ? null : <span className="me-2 pt-1">{peopleLabel}</span>}
      <PersonList
        persons={people}
        small={false}
        sep=""
        finalSep={<span className="mx-1 mt-1">and</span>}
        showLastName={showLastName}
      />
    </div>
  );
}

function Lead({ lead }) {
  return !lead ? null : (
    <div className="lead px-0">
      <ReactMarkdown>{lead}</ReactMarkdown>
    </div>
  );
}

function Fields({ fields }) {
  return !fields ? null : (
    <ul className="list-group list-group-horizontal flex-wrap gap-1">
      {Object.keys(fields).map((key) => (
        <li className="list-group-item py-2" key={key}>
          <span className="key text-primary opacity-50">{`${key}: `}</span>
          <span className="value text-primary">
            <ReactMarkdown components={{ p: React.Fragment }}>
              {fields[key]}
            </ReactMarkdown>
          </span>
        </li>
      ))}
    </ul>
  );
}

function Links({ links }) {
  return !links ? null : (
    <div className="d-flex gap-2 flex-wrap">
      {links.map((link) => (
        <AutoLink
          to={link.href}
          key={link.href}
          className={link.className || "btn btn-primary"}
        >
          {link.text}
        </AutoLink>
      ))}
    </div>
  );
}

export default function PageHeader({
  title,
  subtitle,
  tagline,
  lead,
  people,
  peopleLabel,
  date,
  dateModified,
  children,
  fields,
  links,
  featuredImage = {},
  options = {},
  ...props
} = {}) {
  // Options
  const defaultOpts = {
    darkMode: true,
    showFields: true,
    showPeople: true,
    showDateModified: true,
    showDate: true,
    showLead: true,
    showTitle: true,
    showSubtitle: true,
    showTagline: true,
    showLinks: true,
    showFeaturedImage: true,
    showFeaturedImageLeftOfTitle: false,
    showFeaturedImageBelowHeader: true,
  };

  const {
    darkMode,
    showDate,
    showDateModified,
    showTitle,
    showSubtitle,
    showTagline,
    showPeople,
    showLead,
    showLinks,
    showFields,
    showFeaturedImage,
    showFeaturedImageBelowHeader,
    showFeaturedImageLeftOfTitle,
    showLastName,
  } = { ...defaultOpts, ...options };
  const showInfo =
    (showDate && Boolean(date)) ||
    (showDateModified && Boolean(dateModified)) ||
    (showTitle && Boolean(title)) ||
    (showSubtitle && Boolean(subtitle)) ||
    (showPeople && Boolean(people)) ||
    (showLead && Boolean(lead)) ||
    (showLinks && Boolean(links)) ||
    (showFields && Boolean(fields)) ||
    (showFeaturedImage &&
      showFeaturedImageLeftOfTitle &&
      Boolean(featuredImage));
  const showHeader = showInfo || (showFeaturedImage && Boolean(featuredImage));

  return !showHeader ? null : (
    <header {...props}>
      {!showInfo ? null : (
        <div
          className={`position-relative pt-0
            ${darkMode ? "dark-mode text-light" : "text-primary"}
            `}
        >
          <Circles />
          <div
            className="container pb-3 pb-md-5 position-relative"
          >
            <div className="row" style={{ paddingTop: "7rem" }}>
              <div className="col col-5 col-md-4 col-lg-3 pt-2 pe-3 pb-5">
                {/* A spacer to align the image with the title */}
                {(!showDate || !date) &&
                (!showDateModified || !dateModified) ? null : (
                  <div className="mb-lg-5"></div>
                )}
                <FeaturedImageNextToTitle
                  imageObj={
                    showFeaturedImage && showFeaturedImageLeftOfTitle
                      ? featuredImage
                      : undefined
                  }
                />
              </div>
              <div className="col col-12 col-lg-7 d-flex flex-column gap-3">
                <Date
                  date={showDate ? date : null}
                  dateModified={showDateModified ? dateModified : null}
                />
                <Title
                  title={showTitle ? title : null}
                  subtitle={showSubtitle ? subtitle : null}
                  tagline={showTagline ? tagline : null}
                />
                {showPeople && (
                  <People
                    people={people}
                    peopleLabel={peopleLabel}
                    showLastName={showLastName}
                  />
                )}
                {showLead && <Lead lead={lead} />}
                {showFields && <Fields fields={fields} />}
                {showLinks && <Links links={links} />}
              </div>
            </div>
            {children}
          </div>
        </div>
      )}

      {!showFeaturedImage ||
      !featuredImage ||
      !showFeaturedImageBelowHeader ? null : (
        <FeaturedImage {...featuredImage} />
      )}
    </header>
  );
}
